/**
 * The screen at the other end of an invite link.
 *
 * It is the one screen in this app a stranger can be on. Everything else is
 * behind the sign-in card in `App.tsx`, because there is nothing for somebody
 * with no session to render; here there is, and it matters that there is —
 * somebody who has just been sent a link has to be able to see what it is
 * before they are asked to sign in to something. So this renders first and the
 * sign-in card renders underneath it, and `signInUrl` carries this path as
 * `next`, so signing in comes straight back here rather than to the home
 * screen of a group they are not in yet.
 *
 * **A claim link is a warning before it is a button.** Following one hands
 * somebody the row a group has been splitting expenses with — and if they are
 * already signed in to an account of their own, it pours that account into the
 * same row and there is no request that takes it back. So the sentence saying
 * so is above the button, the confirmation is a checkbox rather than a second
 * click on the same control, and the server refuses without
 * `?acknowledgeMerge=true` in any case: the tick is the courtesy and the query
 * parameter is the enforcement, exactly as with every other limit here.
 *
 * **And it says what does not happen, because that is what people are
 * worried about.** Claiming moves no balance by a cent. Every expense already
 * split with that person stays split exactly as it is, because the row those
 * lines point at is the row being handed over — nothing is re-entered and
 * nothing is recalculated. `server/invites.ts` is where that is true; this is
 * where it is said.
 */

import { useCallback, useState } from 'react'
import { useAcceptInvite, useInviteLanding } from '../queries'
import { SignIn } from './SignIn'

/** What each dead state means, for somebody holding a link that will not work. */
const DEAD: Record<string, string> = {
  revoked: 'That link has been revoked by somebody in the group.',
  expired: 'That link has expired. Invite links last seven days — ask for another.',
  used: 'That link has already been used. Ask for another.',
}

export function Invite({ token }: { token: string }) {
  const landing = useInviteLanding(token)
  const accept = useAcceptInvite(token)
  const [understood, setUnderstood] = useState(false)

  const invite = landing.data?.invite
  const you = landing.data?.you

  /**
   * A hard navigation rather than `navigate()`, and that is the point of it.
   * Claiming changes which account this browser is — the user id afterwards is
   * the placeholder's — so every answer already in the query cache was fetched
   * as somebody else. Reloading re-reads the session and everything under it
   * from a clean start, which is the cheap and complete version of a cache
   * surgery that would otherwise be a list of things to forget.
   */
  const follow = useCallback(async () => {
    const result = await accept.mutateAsync({ acknowledgeMerge: understood })
    window.location.assign(`/groups/${encodeURIComponent(result.group.id)}`)
  }, [accept, understood])

  if (landing.isPending) return <p className="muted">Reading that link…</p>

  if (landing.isError) {
    return (
      <section className="card">
        <h2>That link</h2>
        <p className="bad">{landing.error.message}</p>
      </section>
    )
  }

  if (!invite || !you) return null

  const claiming = invite.kind === 'claim'
  const name = invite.placeholder?.displayName ?? null
  const inviter = invite.invitedByDisplayName ?? 'Somebody'
  const dead = invite.state === 'live' ? null : DEAD[invite.state]
  const alreadyClaimed = invite.placeholder?.claimed === true

  return (
    <>
      <section className="card">
        <h2>
          {inviter} invited you to {invite.group.name}
        </h2>

        {claiming && name !== null && (
          <>
            <p>
              You are being handed <strong>{name}</strong>’s place in the group.
            </p>
            {/*
              The reassurance is the substance, not decoration. The one thing
              somebody wonders when an app offers to merge two things is what
              it is about to do to the numbers, and the answer here is nothing
              at all — the lines already point at the row being handed over.
            */}
            <p className="muted">
              Everything already recorded against {name} stays exactly as it is: every expense
              they were split into, and every cent of what that leaves them owing or owed.
              Nothing is re-entered and no balance moves.
            </p>
          </>
        )}
        {!claiming && (
          <p className="muted">
            Following this link adds you to {invite.group.name}. You will see its expenses and
            where everybody stands.
          </p>
        )}

        {invite.group.archivedAt !== null && (
          <p className="badge">This group is archived, and takes no new members.</p>
        )}

        {dead && <p className="bad">{dead}</p>}
        {!dead && alreadyClaimed && (
          <p className="bad">
            {name} has an account already — somebody has claimed this place. If that was not
            you, ask whoever sent the link.
          </p>
        )}

        {!dead && !alreadyClaimed && !you.signedIn && (
          <p className="muted">Sign in below, and this link will still be waiting.</p>
        )}

        {!dead && !alreadyClaimed && you.signedIn && (
          <>
            {you.alreadyInGroup && !claiming ? (
              <>
                <p className="ok">You are already in this group.</p>
                <p className="actions">
                  <a className="button" href={`/groups/${encodeURIComponent(invite.group.id)}`}>
                    Open {invite.group.name}
                  </a>
                </p>
              </>
            ) : (
              <>
                {/*
                  The merge warning. It is shown to an account that already has
                  a history here and to nobody else, because somebody signing
                  in for the first time has nothing to lose and a warning they
                  cannot act on is a warning they learn to click through.
                */}
                {you.mergesAccount && (
                  <div className="nudge">
                    <p className="nudge__what">
                      You are signed in as <strong>{you.displayName}</strong>, an account that is
                      already in {you.groupCount} {you.groupCount === 1 ? 'group' : 'groups'}.
                      Claiming {name}’s place moves all of it — those groups, their expenses and
                      their balances — onto {name}’s row, and the two cannot be separated again.
                      If that is not what you meant, sign out first and follow the link as
                      yourself.
                    </p>
                    <label className="plan__switch">
                      <input
                        type="checkbox"
                        checked={understood}
                        onChange={(event) => setUnderstood(event.target.checked)}
                      />
                      I understand this cannot be undone
                    </label>
                  </div>
                )}

                <p className="actions">
                  <button
                    type="button"
                    disabled={
                      accept.isPending ||
                      (you.mergesAccount && !understood) ||
                      invite.group.archivedAt !== null
                    }
                    onClick={() => void follow()}
                  >
                    {claiming ? `Claim ${name}’s place` : `Join ${invite.group.name}`}
                  </button>
                </p>
              </>
            )}
            {accept.isError && <p className="bad">{accept.error.message}</p>}
          </>
        )}
      </section>

      {!you.signedIn && <SignIn signInError={null} />}
    </>
  )
}
